import React, { useState } from 'react';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { injectTSDI as tsdi } from '../../../../core/tsdi';
import { Api } from '../../../../core/api';
import { WorkoutFlagStatus } from '../../../../core/api/FitnessActivityClient';

interface UserFlagPromptDialogProps {
    open: boolean;
    onClose: () => void;
    userFlagId: number;
    onSaved: () => void;
}

const statusOptions = Object.values(WorkoutFlagStatus).map((status) => ({
    label: status,
    value: status
}));

export function UserFlagPromptDialog({
    open,
    onClose,
    userFlagId,
    onSaved
}: UserFlagPromptDialogProps) {
    const { fitnessActivityClient } = tsdi(Api);
    const [status, setStatus] = useState<WorkoutFlagStatus>(
        WorkoutFlagStatus.SUSPICIOUS
    );
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await fitnessActivityClient.admin.userFlagControllerUpdateUserFlag(
                userFlagId,
                {
                    id: userFlagId,
                    userId: '',
                    status,
                    comment,
                    createdBy: '',
                    activityIds: []
                }
            );
            onSaved();
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const handleSkip = () => {
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>User Flag Created</DialogTitle>
                    <DialogDescription>
                        This is the first flagged activity for this user. A
                        user-level flag has been auto-created with status
                        SUSPICIOUS. You can adjust the user-level flag below.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <Select
                        label="Status"
                        value={status}
                        options={statusOptions}
                        onChange={(value: WorkoutFlagStatus) =>
                            setStatus(value)
                        }
                    />
                    <TextField
                        label="Comment"
                        type="text"
                        multiline
                        value={comment}
                        onChange={(value) => setComment(String(value))}
                    />
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={handleSkip}
                        disabled={saving}
                    >
                        Skip for now
                    </Button>
                    <Button onClick={handleSave} loading={saving}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
